import { Box, Card, Container, IconButton, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { memo, useCallback, useEffect, useState, useMemo } from "react";
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { Device, EldAssetNotification, EldLandmark } from "src/models";
import useModelData from "src/hooks/use-model-data";
import Iconify from "src/components/iconify/iconify";
import { DisplayPageData } from "src/components/display-page-data";
import { formatDatetimeForPreview } from "src/utils/date-utils";
import { useAuthContext } from "src/auth/hooks";

import PageHeader from "src/components/page-header";
import NoneFound from "src/components/none-found";
import { useSort } from "src/hooks/use-sort";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useParams } from "src/routes/hooks";
import { useSearch } from "src/hooks/use-search";
import { SearchCard } from "src/components/search-card";
import dayjs from "dayjs";

type EldHistoryProps = {
  lines?: EldAssetPerLandmarkHistoryLine[]
}

const EldHistory = memo(({ lines }: EldHistoryProps) => {
  const { session } = useAuthContext();
  const timezone = session?.organization?.companyTimezone as string;
  const { sortedList, sortData } = useSort(lines);

  const {
    searchText,
    setSearchText,
    filteredList,
  } = useSearch(sortedList, ["deviceType"]);

  if (!lines) {
    return null;
  }

  return <div>
    <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="Devices" />
    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TruxTableCell>
                Device Type
              </TruxTableCell>
              <TruxTableCell>
                Asset
              </TruxTableCell>
              <TruxTableCell>
                Entered
              </TruxTableCell>
              <TruxTableCell>
                Left
              </TruxTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredList.map((line, index) => {
              const device = line;

              let deviceType = "";
              if (device.deviceType.startsWith("ELD Truck")) {
                deviceType = "Truck";
              } else if ((device.externalIdentifier || "").startsWith("1|")) {
                deviceType = "CT Trailer";
              } else if ((device.externalIdentifier || "").startsWith("2|")) {
                deviceType = "Samsara";
              } else if ((device.externalIdentifier || "").startsWith("3|")) {
                deviceType = "SkyBitz";
              } else if ((device.externalIdentifier || "").startsWith("4|")) {
                deviceType = "Premier";
              } else if ((device.externalIdentifier || "").startsWith("5|")) {
                deviceType = "CT Imobilizer";
              }
              return <TableRow key={index}>
                <TruxTableCell>
                  {deviceType}
                </TruxTableCell>
                <TruxTableCell>
                  {device.deviceType}
                </TruxTableCell>
                <TruxTableCell>
                  {line.inAssetNotification && formatDatetimeForPreview(line.inAssetNotification.notificationTime, timezone)}
                </TruxTableCell>
                <TruxTableCell>
                  {line.outAssetNotification && formatDatetimeForPreview(line.outAssetNotification.notificationTime, timezone)}
                </TruxTableCell>
              </TableRow>;
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type PageModelBase = {
  eldLandmark: EldLandmark,
}

type PageModel = {
  lines: EldAssetPerLandmarkHistoryLine[]
}

type EldAssetPerLandmarkHistoryLine = Device & {
  inAssetNotification?: EldAssetNotification,
  outAssetNotification?: EldAssetNotification,
}

export default function Page() {
  const settings = useSettingsContext();
  const { eldLandmarkId } = useParams();
  const { session } = useAuthContext();
  const timezone = session?.organization?.companyTimezone as string;

  const today = useMemo(() => dayjs().tz(timezone).format("YYYY-MM-DD"), [timezone]);
  const [fromDate, setFromDate] = useState(() => dayjs(today).add(-21, 'days').format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(() => today);


  const dataLoaderBase = useCallback(() => ({
    eldLandmark: axios.get(
      formatEndpoint(endpoints.eldLandmarks.findById, {id: eldLandmarkId})
    ).then(response => response.data),
  }), [eldLandmarkId]);


  const {
    data: baseData,
    updateData: baseUpdateData,
    setData: baseSetData,
    error: baseError,
    loaded: baseLoaded,
    loading: baseLoading,
    setShouldRefresh: baseSetShouldRefresh,
  } = useModelData<PageModelBase>(dataLoaderBase);


  const dataLoader = useCallback(() => ({
    lines: !fromDate || !fromDate || !eldLandmarkId
      ? []
      : axios.get(
        formatEndpoint(endpoints.devices.findAllByLandmarkHistory, {
          fromDate,
          toDate,
          eldLandmarkId,
        })
      ).then(response => response.data),
  }), [fromDate, toDate, eldLandmarkId]);

  const {
    data,
    updateData,
    setData,
    error,
    loading,
    setShouldRefresh,
  } = useModelData<PageModel>(dataLoader);


  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);


  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading || baseLoading} error={error} data={data}>
        {baseData && <PageHeader title={`Assets history for '${baseData.eldLandmark.name}'`} subtitle={<Box sx={{display: "flex", alignItems: "center"}}>
          <IconButton onClick={() => {
            setFromDate(dayjs(fromDate).add(-21, 'days').format("YYYY-MM-DD"))
            setToDate(dayjs(toDate).add(-21, 'days').format("YYYY-MM-DD"))
            setShouldRefresh(true);
          }} size="small" sx={{p: 0}}> <Iconify icon="eva:arrow-ios-back-fill" /> </IconButton>
          {dayjs(fromDate).format("MM/DD/YYYY")} {dayjs(toDate).format("MM/DD/YYYY")}
          {toDate !== today && <IconButton size="small" sx={{p: 0}} onClick={() => {
            setFromDate(dayjs(fromDate).add(21, 'days').format("YYYY-MM-DD"))
            setToDate(dayjs(toDate).add(21, 'days').format("YYYY-MM-DD"))
            setShouldRefresh(true);
          }}> <Iconify icon="eva:arrow-ios-forward-fill" /> </IconButton>}
        </Box>} />}

        {data && data?.lines?.length === 0 && baseData &&
          <NoneFound title={`No Notifications Found in ${baseData.eldLandmark.name}`} message={`When devices enter ${baseData.eldLandmark.name} they will appear here.`} />
        }

        {data && data?.lines?.length !== 0 && <EldHistory lines={data?.lines} />}
      </DisplayPageData>
    </Container>
  );
}
